import { Collapsible } from '@base-ui/react/collapsible';
import { NavRoute } from '@web-app/components/Navbar/types';
import { Badge } from '@web-app/components/ui/Badge/badge';
import { useLocalStorage } from '@web-app/hooks/useLocalStorage';
import { cn } from '@web-app/lib/utils';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import { LuChevronDown } from 'react-icons/lu';

interface CollapsibleNavItemProps {
  route: NavRoute;
  children: ReactNode;
  /** Aggregate badge count from children, shown on the collapsed group. */
  notificationCount?: number;
}

export const CollapsibleNavItem = ({
  route,
  children,
  notificationCount = 0,
}: CollapsibleNavItemProps): JSX.Element => {
  const router = useRouter();

  // Check if any child route is active
  const isChildActive = route.children?.some(
    (child) => router.route === child.path,
  );

  // Persist collapsed state in localStorage
  const [storedIsOpen, setStoredIsOpen] = useLocalStorage(
    `navbar_collapsed_${route.path}`,
    false,
  );

  // Always open when child is active, otherwise use stored state
  const isOpen = isChildActive || storedIsOpen;

  // Close when navigating to a route outside this collapsible's children
  useEffect(() => {
    if (!isChildActive) {
      setStoredIsOpen(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [router.route]);

  const handleOpenChange = (open: boolean) => {
    // Prevent closing when a child route is active
    if (isChildActive) {
      return;
    }
    setStoredIsOpen(open);
  };

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      className="w-full"
    >
      <Collapsible.Trigger
        type="button"
        disabled={isChildActive}
        className={cn(
          'flex w-full items-center gap-2 rounded-[10px] px-4 py-2',
          'text-base font-normal text-sidebar-foreground',
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring',
          'disabled:cursor-default',
          '[&>svg:first-child]:size-6',
        )}
      >
        {route.icon}
        <span className="flex flex-1 items-center gap-2 text-left">
          {route.name}
          {notificationCount > 0 && !isOpen && (
            <Badge
              variant="destructive"
              size="sm"
              data-testid="nav-group-notification"
            >
              {notificationCount}
            </Badge>
          )}
        </span>
        <LuChevronDown
          className={cn(
            'size-[18px] shrink-0 transition-transform duration-200 [&_path]:stroke-[1.5]',
            isOpen && 'rotate-180',
          )}
        />
      </Collapsible.Trigger>

      <Collapsible.Panel
        id={`collapsible-${route.path}`}
        className={cn(
          'overflow-hidden',
          'h-[var(--collapsible-panel-height)] transition-[height] duration-200 ease-in-out',
          'data-[starting-style]:h-0 data-[ending-style]:h-0',
        )}
      >
        <div className="px-7">
          <div className="flex flex-col gap-2 border-l border-solid border-sidebar-border px-2.5 py-0.5">
            {children}
          </div>
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
};
